<template>
  <simple-dialog
      title-text="参照先共通辞書編集"
      :value="dialogVisible"
      no-padding
      @cancel="handleCancel"
      @apply="handleApply"
      full-height>
    <template #prepend>
      <v-toolbar style="flex-grow: 0;" flat>
        <v-banner single-line class="header-banner" v-if="hasDictionaryVersionUpdate">
          <error-icon #icon level="info"></error-icon>
          共通辞書参照のアップデートを行う為、完了の際に辞書への変更が適用されます。
        </v-banner>
        <v-spacer v-else></v-spacer>
        <v-toolbar-items style="width: 300px;">
          <v-row
              dense
              align="center"
              justify="center">
            <v-col>
              <v-text-field
                  v-model="search"
                  placeholder="検索"
                  outlined
                  dense
                  clearable
                  hide-details>
              </v-text-field>
            </v-col>
          </v-row>
        </v-toolbar-items>
      </v-toolbar>
    </template>

    <el-table
        :data="categorizedFilteredDictionariesView"
        :span-method="buildTableSpan"
        :row-class-name="({row}) => row.header ? 'header-row' : ''"
        row-key="id"
        default-expand-all>
      <el-table-column
          label="参照"
          prop="isSelected"
          width="60"
          header-align="center"
          align="left">
        <template #default="{row}">
          <template v-if="row.header">
            <header>{{ row.header }}</header>
          </template>
          <template v-else>
            <div style="text-align: center;">
              <table-inline>
                <el-checkbox
                    :value="row.isSelected"
                    @change="handleSelectionChange(row, $event)"
                    :indeterminate="row.isSelected && (!row.isLatestVersion || row.isDeleted)">
                </el-checkbox>
              </table-inline>
            </div>
          </template>
        </template>
      </el-table-column>
      <el-table-column
          label="デフォルト"
          prop="isDefault"
          width="100"
          align="center">
        <template #default="{row}">
          <el-radio
              :value="row.isDefault"
              :disabled="!row.isSelected || !!row.isDeleted"
              :label="true"
              @change="handleDefaultChange(row)"
              class="no-label"/>
        </template>
      </el-table-column>
      <el-table-column
          width="140"
          prop="id"
          label="ID">
        <template #default="{row}">
          <template v-if="!row.header">
            <table-inline>
              <hds-id-field :value="row.id"/>
            </table-inline>
          </template>
          <template v-else>&nbsp;</template>
        </template>
      </el-table-column>
      <el-table-column
          min-width="35"
          prop="name"
          label="辞書名">
        <template #default="{row}">
          <template v-if="row.isDeleted">
            <deletable-field
              :value="row.name"
              deleted>
            </deletable-field>
          </template>
          <template v-else-if="!row.isLatestVersion">
            <deletable-field
                :value="row.name"
                content="旧バージョンの辞書です"
                icon="el-icon-info"
                icon-color="light-blue darken-2"
                deleted>
            </deletable-field>
          </template>
          <template v-else>
            {{ row.name }}
          </template>
        </template>
      </el-table-column>
      <el-table-column
          prop="description"
          label="説明">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          prop="owner"
          label="事業者">
        <template #default="{row}">
          <table-inline>
            <owner-field
                :value="row.ownerId"
                :value-handler="owner => row.ownerName = owner.name || ''"
                hide-details>
            </owner-field>
          </table-inline>
        </template>
      </el-table-column>
    </el-table>
  </simple-dialog>
</template>

<script>
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import query from "@/gql/DictionaryList/Query.gql";
import TableInline from "@/components/atoms/TableInline.vue";
import HdsIdField from "@/components/molecules/HdsIdField.vue";
import OwnerField from "@/components/molecules/OwnerField.vue";
import BetterSet from "betterset";
import IdInfo from "@/store/modules/hds/IdInfo";
import natsort from "natsort";
import DeletableField from "@/components/molecules/DeletableField.vue";
import ErrorIcon from "@/components/molecules/ErrorIcon.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

const sortFn = natsort();

export default {
  name: "DictionaryReferenceDialog",
  components: {TextFieldLike, ErrorIcon, DeletableField, OwnerField, HdsIdField, TableInline, SimpleDialog},
  props: {
    value: {
      type: Boolean,
      default: false
    },
    referenceDictionaries: {
      type: Array,
      required: true
    },
    defaultDictionaryId: {
      type: String,
      required: false
    },
    dictionaryId: {
      type: String,
      required: false
    },
  },
  data() {
    return {
      /** ダイアログの表示・非表示状態 */
      dialogVisible: false,
      /** 取得した辞書リスト */
      dictionaries: [],
      /** テーブル表示用の辞書リスト */
      dictionariesView: [],
      selectedDictionaryIds: new BetterSet(),
      selectedDefaultDictionaryId: '',
      starredDictionaries: [],
      search: '',
      hasDictionaryVersionUpdate: false,
    };
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    filteredDictionariesView() {
      const search = this.search && this.search.trim();
      if(search) {
        return this.dictionariesView.filter(d => {
          if(d.name.includes(search)) return true;
          if(d.description.includes(search)) return true;
          if(d.ownerName.includes(search)) return true;
        });
      } else {
        return this.dictionariesView;
      }
    },
    categorizedFilteredDictionariesView() {
      const starredDictionaries = [];
      const otherDictionaries = this.filteredDictionariesView.filter(d => {
        if (this.isStarredDictionary(d)) {
          starredDictionaries.push(d);
          return false;
        }
        return true;
      });

      const result = [];
      if(starredDictionaries.length) {
        result.push({
          id: 'starred',
          header: 'スター付き',
          children: starredDictionaries
        });
      }
      if(otherDictionaries.length) {
        result.push({
          id: 'others',
          header: 'その他の辞書',
          children: otherDictionaries
        });
      }
      return result;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
    }),
    async load() {
      await this.loadStarredDictionaries();
      const {dictionary} = await this.gqlQuery({
        query,
        variables: {
          dictionaryTypes: ['common'],
          workingCopy: false
        }
      });
      this.dictionaries = dictionary;
      this.updateViewDictionaries();
    },
    async loadStarredDictionaries() {
      const {data: starredDictionaries} = await this.hcsClient.get('/starred_dictionaries');
      this.starredDictionaries = starredDictionaries;
    },
    handleSelectionChange(row, selected) {
      if(row.isDefault) {
        this.showSnackbar({
          visible: true,
          color: 'warn',
          message: 'デフォルトの共通辞書は参照解除できません'
        });
        return;
      }

      if(selected) {
        const addedIdInfo = IdInfo.of(row.id);
        for(const selectedId of this.selectedDictionaryIds) {
          const selectedIdInfo = IdInfo.of(selectedId);
          if(addedIdInfo.elementId === selectedIdInfo.elementId) {
            this.selectedDictionaryIds.delete(selectedId);
            if(this.selectedDefaultDictionaryId === selectedId) {
              this.selectedDefaultDictionaryId = row.id;
            }
          }
        }
        this.selectedDictionaryIds.add(row.id);
      } else {
        this.selectedDictionaryIds.delete(row.id);
      }

      this.hasDictionaryVersionUpdate = (() => {
        for(const rd of this.referenceDictionaries) {
          const rdIdInfo = IdInfo.of(rd.id);
          for(const sdId of this.selectedDictionaryIds) {
            const sdIdInfo = IdInfo.of(sdId);
            if(sdIdInfo.elementId === rdIdInfo.elementId && sdIdInfo.version !== rdIdInfo.version) {
              return true;
            }
          }
        }
        return false;
      })();

      this.updateViewDictionaries();
    },
    handleDefaultChange(row) {
      this.selectedDefaultDictionaryId = row.id;
      this.updateViewDictionaries();
    },
    updateViewDictionaries() {
      const allDictionaryMap = [...this.dictionaries, ...this.referenceDictionaries]
          .reduce((map, dictionary) => {
            map.set(dictionary.id, dictionary);
            return map;
          }, new Map());

      const latestDictionaryVersionSet = (() => {
        const latestDictionaryVersionMap = new Map();
        for(const id of allDictionaryMap.keys()) {
          const idi = IdInfo.of(id);
          if(latestDictionaryVersionMap.has(idi.elementId)) {
            latestDictionaryVersionMap.set(idi.elementId, Math.max(latestDictionaryVersionMap.get(idi.elementId), idi.version));
          } else {
            latestDictionaryVersionMap.set(idi.elementId, idi.version);
          }
        }
        const latestDictionaryVersionSet = new Set();
        for(const [elementId, version] of latestDictionaryVersionMap.entries()) {
          latestDictionaryVersionSet.add(`${elementId}:${version}`)
        }
        return latestDictionaryVersionSet;
      })();

      this.dictionariesView = Array.from(allDictionaryMap.values())
          .map(rd => ({
            ...rd,
            ownerName: '',
            isSelected: this.selectedDictionaryIds.has(rd.id),
            isDefault: rd.id === this.selectedDefaultDictionaryId,
            isLatestVersion: latestDictionaryVersionSet.has(rd.id),
          }))
          .sort((a, b) => sortFn(a.name, b.name));
    },
    updateSelectedDictionaryIds() {
      this.selectedDictionaryIds.clear();
      if(Array.isArray(this.referenceDictionaries)) {
        this.referenceDictionaries.forEach(v => {
          this.selectedDictionaryIds.add(v.id);
        });
      }
      this.updateViewDictionaries();
    },
    handleCancel() {
      this.$emit('input', false);
    },
    handleApply() {
      const referenceDictionaryIds = Array.from(this.selectedDictionaryIds.keys())
      const defaultDictionaryId = this.selectedDefaultDictionaryId;
      const hasDictionaryVersionUpdate = this.hasDictionaryVersionUpdate;

      this.$emit('apply', {referenceDictionaryIds, defaultDictionaryId, hasDictionaryVersionUpdate});
      this.$emit('input', false);
    },
    /**
     * テーブルの結合状態を生成します。
     * ヘッダ行の場合に全列を結合します。
     *
     * @param row 行データ
     * @param columnIndex 列インデックス
     * @return {{colspan: number, rowspan: number}} 行・列のセルの結合数
     */
    buildTableSpan({row, columnIndex}) {
      if(columnIndex === 0 && row.header) {
        return {
          rowspan: 1,
          colspan: 6
        };
      }
    },
    findStarredDictionary(dictionary) {
      const elementId = IdInfo.of(dictionary.id).elementId;
      const index = this.starredDictionaries.findIndex(sd => sd.dictionaryId === elementId);
      return {
        dictionaryElementId: elementId,
        index,
        starredDictionary: index !== -1 ? this.starredDictionaries[index] : null
      };
    },
    isStarredDictionary(dictionary) {
      return this.findStarredDictionary(dictionary).index !== -1;
    },
  },
  watch: {
    referenceDictionaries() {
      this.updateSelectedDictionaryIds();
    },
    defaultDictionaryId(val) {
      this.selectedDefaultDictionaryId = val;
      this.updateViewDictionaries();
    },
    value: {
      immediate: true,
      async handler(newVal, oldVal) {
        if(newVal === oldVal) {
          return;
        }
        if(newVal) {
          const loading = this.$loading({
            lock: true,
            text: '参照先共通辞書を読込中...',
            spinner: 'el-icon-loading',
          });
          try {
            await this.load();
            this.hasDictionaryVersionUpdate = false;
            this.selectedDefaultDictionaryId = this.defaultDictionaryId;
            this.updateSelectedDictionaryIds();
            this.dialogVisible = true;
          } finally {
            loading.close();
          }
        } else {
          this.dialogVisible = false;
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .el-table__indent,
  .el-table__placeholder {
    display: none;
  }

  .header-row {
    header {
      margin-left: 10px;
      font-weight: bold;
      display: inline;
    }

    &.hover-row td {
      background-color: inherit;
    }
  }
}

.v-banner.v-sheet.header-banner {
  flex-grow: 1;

  ::v-deep .v-banner__wrapper {
    border-bottom: 0 none;
  }
}
</style>
